import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useProjectMilestones } from '../../../../api/hooks/useProjects';

interface Milestone {
  id: string;
  name: string;
  deadline: string | null;
  state: string;
  rank: string;
}

const DONE_STATES = new Set(['COMPLETED', 'CANCELED']);

/**
 * Nächste Meilensteine — künftige, offene Termine sortiert nach Deadline (Top 4),
 * mit Restlaufzeit-Anzeige (Clickdummy-Dashboard "Nächste Meilensteine").
 */
export function DashboardMilestones({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { data } = useProjectMilestones(projectId);

  const upcoming = useMemo(() => {
    const raw = (data as unknown as { data?: Milestone[] } | undefined)?.data ?? [];
    const today = dayjs().startOf('day');
    return raw
      .filter((m) => m.deadline && !DONE_STATES.has(m.state) && dayjs(m.deadline).isAfter(today.subtract(1, 'day')))
      .sort((a, b) => dayjs(a.deadline!).valueOf() - dayjs(b.deadline!).valueOf())
      .slice(0, 4);
  }, [data]);

  if (upcoming.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)', py: 1 }}>
        {t('dashboard.noMilestones', 'Keine kommenden Meilensteine')}
      </Typography>
    );
  }

  return (
    <Stack>
      {upcoming.map((m, i) => {
        const days = dayjs(m.deadline!).startOf('day').diff(dayjs().startOf('day'), 'day');
        const isEnd = m.rank === 'END';
        return (
          <Box
            key={m.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.25,
              borderBottom: i < upcoming.length - 1 ? '1px dashed var(--pm-bg-deep)' : 'none',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                {isEnd ? (
                  <FlagIcon sx={{ fontSize: 15, color: 'var(--pm-fg-accent)' }} />
                ) : (
                  <EmojiEventsIcon sx={{ fontSize: 15, color: 'var(--pm-fg-subtle)' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {m.name}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                {dayjs(m.deadline!).format('DD.MM.YYYY')}
                {isEnd ? ` · ${t('dashboard.endMilestone', 'Endtermin')}` : ''}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: days <= 14 ? 'var(--pm-warning)' : 'var(--pm-fg-subtle)',
              }}
            >
              {t('dashboard.inDays', 'in {{n}}T', { n: days })}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}
